import { useCallback, useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import InactivityDialog from "./InactivityDialog";
import SessionService from "../../services/SessionService";

/**
 * Session-expiry UX, driven by ONE clock: the server's.
 *
 * The server owns session lifetime (a sliding idle window plus an absolute lifetime cap, both from
 * MTC:Session config). The client merely mirrors the expiry — via the passive `/session/status`
 * heartbeat — so it can render a truthful countdown.
 *
 * This deliberately no longer runs its own idle timer as the source of truth. It used to: a
 * client-side react-idle-timer decided when you were logged out, on a clock entirely independent of
 * the server's. The two drifted, and users were bounced to /login with no warning at all.
 *
 * react-idle-timer is still used, but only to detect genuine human activity (mouse/keyboard). A
 * user can plainly be present while making no API calls — reading a long note — and we don't want
 * to log them out for that. On real interaction we send a throttled "I'm still here" to the server.
 */

// At most one keep-alive per this interval while the user is interacting. Any real API call the
// user triggers already slides the session (SessionMiddleware), so this only covers the
// clearly-present-but-not-calling-the-API case.
const EXTEND_THROTTLE_MS = 60 * 1000;

// How often we re-read the mirrored expiry to update the countdown.
const TICK_MS = 1000;

export default function InactivityHandler() {
  const sessionService = SessionService.getInstance();
  const [open, setOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [canExtend, setCanExtend] = useState(true);
  const lastExtendRef = useRef(0);

  // The clock ran out: an automatic sign-out, so this browser keeps "remember this device".
  const endSession = useCallback(async () => {
    setOpen(false);
    await sessionService.logout({ keepTrustedDevice: true });
    window.location.href = "/login";
  }, [sessionService]);

  // "Log Out Now" in the warning is the user's own choice, so it forgets the device like any other
  // sign-out they choose (AUD-13). At the absolute session cap the same button reads "Sign In
  // Again" — the session is ending anyway, not by choice — so there it keeps the device.
  const logOutNow = useCallback(async () => {
    setOpen(false);
    await sessionService.logout();
    window.location.href = "/login";
  }, [sessionService]);

  const keepAlive = useCallback(async () => {
    lastExtendRef.current = Date.now();
    const remaining = await sessionService.extend();
    if (remaining <= 0) {
      // Server says the session is gone (expired, or past its absolute cap).
      await endSession();
      return;
    }
    setOpen(false);
  }, [sessionService, endSession]);

  // Genuine user interaction -> tell the server we're still here (throttled).
  const onAction = useCallback(() => {
    if (!sessionService.isAuthenticated()) return;
    // Don't auto-extend once we're at the absolute cap: extending can't buy time, and silently
    // renewing here would hide the "you'll need to sign in again" warning from the user.
    if (!sessionService.getCanExtend()) return;
    if (Date.now() - lastExtendRef.current < EXTEND_THROTTLE_MS) return;
    void keepAlive();
  }, [sessionService, keepAlive]);

  useIdleTimer({
    onAction,
    throttle: 2000, // coalesce raw DOM events; the real gate is EXTEND_THROTTLE_MS
  });

  // The countdown and the warning are driven purely by the mirrored server expiry.
  useEffect(() => {
    const id = setInterval(() => {
      if (!sessionService.isAuthenticated()) return;

      const remainingMs = sessionService.getRemainingTime();
      setRemainingSeconds(Math.max(0, Math.ceil(remainingMs / 1000)));
      setCanExtend(sessionService.getCanExtend());

      if (remainingMs <= 0) {
        void endSession();
        return;
      }

      setOpen(remainingMs <= sessionService.getWarningMs());
    }, TICK_MS);

    return () => clearInterval(id);
  }, [sessionService, endSession]);

  return (
    <InactivityDialog
      open={open}
      remaining={remainingSeconds}
      canExtend={canExtend}
      handleStillHere={keepAlive}
      handleLogoutNow={canExtend ? logOutNow : endSession}
    />
  );
}
